import qs from 'query-string'
import { getStoredLocaleWithFallback } from '@retailer-platform/shared-components/src/utils/intl/intl.utils'
import { clientEnv } from '../../exports/env'
import { getActiveGroups, OneTrustCategory } from '../privacy-compliance/oneTrustConsent.utils'
import { generateRandomString } from './pkce/generateRandomString'
import {
  pkceStateKey,
  pkceCodeVerifierKey,
  authServiceClientId,
  authCallbackRedirectUri,
  responseType,
  scope,
  pkceCodeChallengeMethod,
} from './oauthConstants'
import { pkceChallengeFromVerifier } from './pkce/pkceChallengeFromVerifier'

export interface RedirectToAuthServiceOptions {
  email: string
  keepLoggedIn?: boolean
}

export const redirectToAuthService = async (
  emailOrOptions: string | RedirectToAuthServiceOptions
) => {
  // Support both legacy string signature and new options object
  const { email, keepLoggedIn } =
    typeof emailOrOptions === 'string'
      ? { email: emailOrOptions, keepLoggedIn: undefined }
      : emailOrOptions

  // Generate a random string for state param, which we store and then compare later once we get the response
  const state = generateRandomString()
  localStorage.setItem(pkceStateKey, state)

  // Generate a random string for pkce code verifier param, which we store and then send again when requesting token from code
  const codeVerifier = generateRandomString()
  localStorage.setItem(pkceCodeVerifierKey, codeVerifier)

  const codeChallenge = await pkceChallengeFromVerifier(codeVerifier)

  // Determine analytics consent status from OneTrust
  let analyticsConsent: 'granted' | 'denied' | 'pending' = 'pending'
  if (typeof window !== 'undefined' && window.OnetrustActiveGroups) {
    const activeGroups = getActiveGroups()
    analyticsConsent = activeGroups.includes(`,${OneTrustCategory.PERFORMANCE},`)
      ? 'granted'
      : 'denied'
  }

  /* eslint-disable @typescript-eslint/naming-convention */
  const query: Record<string, string | undefined> = {
    state,
    scope,
    email,
    response_type: responseType,
    client_id: authServiceClientId,
    redirect_uri: authCallbackRedirectUri,
    code_challenge_method: pkceCodeChallengeMethod,
    code_challenge: codeChallenge,
    locale: getStoredLocaleWithFallback(),
    analytics_consent: analyticsConsent,
    // Pass keep_logged_in preference to auth service (convert boolean to string)
    keep_logged_in: keepLoggedIn !== undefined ? String(keepLoggedIn) : undefined,
  }
  /* eslint-enable @typescript-eslint/naming-convention */

  window.location.href = qs.stringifyUrl({
    url: `${clientEnv.PUBLIC_CLIENT_AUTH_SERVICE_BASE_URL}/oauth/authorize`,
    query,
  })
}
